import { Component, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import {
  Company,
  Contact,
  InspectionDto,
  InspectionModel,
  Order,
  SubProject
} from '@authority-labs/api-interfaces';
import { InspectionTypeEnum } from '@authority-labs/enums';
import { nameof } from '@authority-labs/helpers';
import { CertificateDialogService } from '@check-list/app/shared/dialogs/certificate-dialog/certificate-dialog.service';
import { RequestQueryBuilder } from '@nestjsx/crud-request';
import { Observable } from 'rxjs';
import { map, tap } from 'rxjs/operators';
import { OrdersService } from '../../orders/orders.service';
import { InspectionsService } from '../inspections.service';

interface InspectionData {
  subProjectId: number;
  subProjectName: string;
  orderId: number;
  inspectionType: string;
  inspectionId: number;
  inspectionDate?: Date;
  assignee?: string;
  company?: string;
  status?: string;
}

@Component({
  selector: 'authority-labs-select-inspection-dialog',
  templateUrl: './select-inspection-dialog.component.html',
  styleUrls: ['./select-inspection-dialog.component.scss']
})
export class SelectInspectionDialogComponent implements OnInit {
  title = 'בחירת הזמנה';
  inspectionInSameSubproject: InspectionData[] = [];
  inspectionInParentSubproject: InspectionData[] = [];
  inspectionInSameProject: InspectionData[] = [];
  selectedInspectionId: number;
  orderId: number;
  subProjectId: number;
  parentSubProjectId: number;
  projectId: number;
  inspectionType: InspectionTypeEnum;
  allShown = false;
  isLoading: Record<number, boolean> = {};

  constructor(
    public dialogRef: MatDialogRef<Order>,
    @Inject(MAT_DIALOG_DATA)
    {
      orderId,
      subProjectId,
      projectId,
      inspectionType
    }: {
      orderId: number;
      subProjectId: number;
      projectId: number;
      inspectionType: InspectionTypeEnum;
    },
    private ordersService: OrdersService,
    private inspectionsService: InspectionsService,
    private certificateDialog: CertificateDialogService
  ) {
    this.orderId = orderId;
    this.subProjectId = subProjectId;
    this.projectId = projectId;
    this.inspectionType = inspectionType;
  }

  ngOnInit() {
    this.loadInspection(this.subProjectId, 'subProject').subscribe(
      (inspections) => {
        this.inspectionInSameSubproject = this.mapToInspectionData(inspections);

        const parentSubProjectId =
          inspections[0]?.order?.subProject?.parentSubProjectId;
        if (parentSubProjectId) {
          this.parentSubProjectId = parentSubProjectId;
          this.loadInspection(this.parentSubProjectId, 'subProject').subscribe(
            (data) => {
              this.inspectionInParentSubproject =
                this.mapToInspectionData(data);
            }
          );
        }
      }
    );
  }

  onSelect(inspection: InspectionData): void {
    this.selectedInspectionId = inspection.inspectionId;
  }

  chooseAndClose(): void {
    this.dialogRef.close(this.selectedInspectionId);
  }

  loadAllInspections(): void {
    this.allShown = true;
    this.loadInspection(this.projectId, 'project').subscribe((inspections) => {
      this.inspectionInSameProject = this.mapToInspectionData(
        inspections
      ).filter(
        (item) =>
          item.subProjectId !== this.subProjectId &&
          item.subProjectId !== this.parentSubProjectId
      );
    });
  }

  openCertificateDialog(orderId: number): void {
    const query = RequestQueryBuilder.create({
      fields: [
        'createdAt',
        'assignee',
        'approvedBy',
        'version',
        'items',
        'items.inspection',
        'inspection',
        'subProject',
        'subProject.company',
        'planner',
        'contacts',
        'contractor',
        'isApproved'
      ],
      join: [
        {
          field: 'subProject',
          select: [
            nameof<SubProject>('block'),
            nameof<SubProject>('lot'),
            nameof<SubProject>('name'),
            nameof<SubProject>('plot')
          ]
        },
        { field: 'subProject.project' },
        { field: 'subProject.company' },
        { field: 'subProject.project.city' },
        { field: 'planner' },
        { field: 'contacts' },
        { field: 'contractor' },
        { field: 'items', select: ['id'] },
        {
          field: 'items.inspection',
          select: ['id', 'name']
        },
        {
          field: 'inspection'
        }
      ]
    }).query();

    this.ordersService.getOne(orderId, query).subscribe((order) => {
      const mainContact = order.mainContactId
        ? order.contacts.find((c) => c.id === order.mainContactId)
        : undefined;
      const contact = mainContact ?? order.contacts[0] ?? new Contact();

      const inspection = new InspectionModel(order.inspection);
      if (inspection.isDefined()) {
        this.certificateDialog.showInvoice(
          {
            order,
            inspection: inspection,
            planner: order.planner,
            contractor: order.contractor,
            project: order.subProject.project,
            subProject: order.subProject,
            company: order.subProject.company,
            contact
          },
          true
        );
      }
    });
  }

  private getInspectionTypeFilters(): InspectionTypeEnum[] {
    const designTypes = [
      InspectionTypeEnum.InspectionDesign,
      InspectionTypeEnum.InspectionInstallation,
      InspectionTypeEnum.InspectionDesignAndInstallation
    ];
    return designTypes.includes(this.inspectionType)
      ? designTypes
      : [this.inspectionType];
  }

  private loadInspection(
    id: number,
    type: 'subProject' | 'project'
  ): Observable<InspectionDto[]> {
    this.isLoading[id] = true;
    const orderIdField =
      type === 'subProject'
        ? `order.${nameof<Order>('subProjectId')}`
        : `order.${nameof<Order>('projectId')}`;

    const query = RequestQueryBuilder.create()
      .setFilter({
        field: nameof<InspectionDto>('inspectionType'),
        operator: '$in',
        value: this.getInspectionTypeFilters()
      })
      .setFilter({
        field: nameof<InspectionDto>('orderId'),
        operator: '$ne',
        value: this.orderId
      })
      .setFilter({ field: orderIdField, operator: '$eq', value: id })
      .select([
        nameof<InspectionDto>('orderId'),
        nameof<InspectionDto>('inspectionType'),
        nameof<InspectionDto>('inspectionDate')
      ])
      .setJoin([
        {
          field: nameof<InspectionDto>('order'),
          select: [
            nameof<Order>('assignee'),
            nameof<Order>('subProjectId'),
            nameof<Order>('projectId')
          ]
        },
        {
          field: 'order.subProject',
          select: [
            nameof<SubProject>('name'),
            nameof<SubProject>('parentSubProjectId')
          ]
        },
        {
          field: 'order.subProject.company',
          select: [nameof<Company>('name')]
        },
        { field: 'order.status', select: ['name'] }
      ]);

    return this.inspectionsService.getMany(query.query()).pipe(
      map((resp) => resp.data),
      tap(() => (this.isLoading[id] = false))
    );
  }

  private mapToInspectionData(
    inspections: InspectionDto[] = []
  ): InspectionData[] {
    return inspections.map((inspection) => ({
      subProjectId: inspection.order.subProject.id,
      subProjectName: inspection.order.subProject.name,
      orderId: inspection.orderId,
      inspectionType: inspection.inspectionType,
      inspectionId: inspection.id,
      assignee: inspection.order.assignee,
      company: inspection.order.subProject.company?.name,
      inspectionDate: inspection.inspectionDate,
      status: (inspection.order as Order).status?.name
    }));
  }
}
